import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  changePassword,
  deleteLLMKey,
  getLLMKeyInfo,
  updateLLMKey,
  updateProfile,
  updateSecurityQuestion,
  uploadAvatar,
} from '@/api/settings';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { Input } from '@/components/ui/input/input';
import { useAuthStore } from '@/stores/authStore';

import styles from './settings.module.css';

const PRESETS = [
  {
    name: 'SiliconFlow（默认）',
    provider: 'siliconflow',
    base_url: 'https://api.siliconflow.cn/v1',
    model: 'Qwen/Qwen2.5-7B-Instruct',
    vision_model: 'Qwen/Qwen2-VL-7B-Instruct',
  },
  {
    name: 'OpenAI',
    provider: 'openai',
    base_url: 'https://api.openai.com/v1',
    model: 'gpt-4o-mini',
    vision_model: 'gpt-4o',
  },
  {
    name: 'DeepSeek',
    provider: 'deepseek',
    base_url: 'https://api.deepseek.com/v1',
    model: 'deepseek-chat',
    vision_model: '',
  },
  {
    name: '自定义',
    provider: 'custom',
    base_url: '',
    model: '',
    vision_model: '',
  },
];

export function SettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);

  const { data: info, isLoading } = useQuery({
    queryKey: ['llm-key'],
    queryFn: getLLMKeyInfo,
  });

  // AI 设置
  const [provider, setProvider] = useState('siliconflow');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 账号设置
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [securityQuestion, setSecurityQuestion] = useState(user?.security_question || '');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [securitySaving, setSecuritySaving] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarError, setAvatarError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!info) return;
    setProvider(info.provider || 'siliconflow');
    setBaseUrl(info.base_url || '');
    setModel(info.model || '');
    setVisionModel(info.vision_model || '');
  }, [info]);

  useEffect(() => {
    if (user) {
      setNickname(user.nickname || '');
      setSecurityQuestion(user.security_question || '');
    }
  }, [user]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const applyPreset = (name: string) => {
    const preset = PRESETS.find((p) => p.name === name);
    if (!preset) return;
    setProvider(preset.provider);
    setBaseUrl(preset.base_url);
    setModel(preset.model);
    setVisionModel(preset.vision_model);
  };

  const handleSaveLLM = async () => {
    if (!apiKey.trim()) {
      showToast('请输入 API Key');
      return;
    }
    if (!baseUrl.trim()) {
      showToast('请输入 Base URL');
      return;
    }
    if (!model.trim()) {
      showToast('请输入模型名称');
      return;
    }

    setSaving(true);
    try {
      await updateLLMKey({
        api_key: apiKey.trim(),
        provider: provider || 'custom',
        base_url: baseUrl.trim() || undefined,
        model: model.trim() || undefined,
        vision_model: visionModel.trim() || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['llm-key'] });
      setApiKey('');
      showToast('API Key 已保存到当前账号');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLLM = async () => {
    if (!window.confirm('确定要删除已保存的 API Key 吗？删除后将使用系统默认配置。')) {
      return;
    }
    setDeleting(true);
    try {
      await deleteLLMKey();
      await queryClient.invalidateQueries({ queryKey: ['llm-key'] });
      setApiKey('');
      showToast('已删除');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleAvatarClick = () => {
    fileRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件');
      return;
    }
    setAvatarLoading(true);
    try {
      const updated = await uploadAvatar(file);
      updateUser(updated);
      showToast('头像已更新');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '头像上传失败');
    } finally {
      setAvatarLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const updated = await updateProfile({ nickname: nickname.trim() || undefined });
      updateUser(updated);
      showToast('昵称已保存');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword) {
      showToast('请输入旧密码');
      return;
    }
    if (newPassword.length < 6) {
      showToast('新密码至少 6 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('两次输入的新密码不一致');
      return;
    }
    setPasswordSaving(true);
    try {
      await changePassword({ old_password: oldPassword, new_password: newPassword });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('密码已修改，请牢记新密码');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '修改密码失败');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    if (!securityQuestion.trim() || !securityAnswer.trim()) {
      showToast('请填写密保问题与答案');
      return;
    }
    setSecuritySaving(true);
    try {
      const updated = await updateSecurityQuestion({
        security_question: securityQuestion.trim(),
        security_answer: securityAnswer.trim(),
      });
      updateUser(updated);
      setSecurityAnswer('');
      showToast('密保已设置');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '设置密保失败');
    } finally {
      setSecuritySaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>设置</h1>
        <p className={styles.subtitle}>管理你的账号信息、安全设置与 AI Key 配置</p>
      </header>

      <Card>
        <div className={styles.form}>
          <div className={styles.sectionTitle}>账号设置</div>

          <div className={styles.avatarRow}>
            <button
              type="button"
              className={styles.avatarUpload}
              onClick={handleAvatarClick}
              disabled={avatarLoading}
              title="点击更换头像"
            >
              {user?.avatar_url && !avatarError ? (
                <img
                  src={user.avatar_url}
                  alt={user.display_name}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span>{user?.display_name?.[0] || '?'}</span>
              )}
              {avatarLoading && <span className={styles.avatarMask}>上传中</span>}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className={styles.fileInput}
              onChange={handleAvatarChange}
            />
            <div className={styles.avatarHint}>
              <div className={styles.avatarHintTitle}>头像</div>
              <div className={styles.avatarHintDesc}>点击左侧头像上传新图片，支持 jpg / png / gif / webp</div>
            </div>
          </div>

          <div className={styles.grid2}>
            <Input
              label="昵称"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={user?.display_name || '请输入昵称'}
            />
            <div className={styles.readonlyField}>
              <span className={styles.fieldLabel}>账号</span>
              <span className={styles.readonlyValue}>{user?.username}</span>
            </div>
          </div>

          <div className={styles.actions}>
            <Button onClick={handleSaveProfile} loading={profileSaving}>
              保存昵称
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.form}>
          <div className={styles.sectionTitle}>修改密码</div>

          <div className={styles.grid2}>
            <Input
              label="旧密码"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="请输入当前密码"
              autoComplete="current-password"
            />
            <div />
            <Input
              label="新密码"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="至少 6 位"
              autoComplete="new-password"
            />
            <Input
              label="确认新密码"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="再次输入新密码"
              autoComplete="new-password"
            />
          </div>

          <div className={styles.actions}>
            <Button onClick={handleChangePassword} loading={passwordSaving}>
              修改密码
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.form}>
          <div className={styles.sectionTitle}>密保设置</div>
          <p className={styles.sectionDesc}>设置密保问题与答案后，可通过「忘记密码」功能找回账号。</p>

          <div className={styles.grid2}>
            <Input
              label="密保问题"
              value={securityQuestion}
              onChange={(e) => setSecurityQuestion(e.target.value)}
              placeholder="如：你的小学班主任姓什么？"
            />
            <Input
              label="密保答案"
              value={securityAnswer}
              onChange={(e) => setSecurityAnswer(e.target.value)}
              placeholder="请输入答案"
              autoComplete="off"
            />
          </div>

          <div className={styles.actions}>
            <Button onClick={handleSaveSecurity} loading={securitySaving}>
              {user?.security_question ? '更新密保' : '设置密保'}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.form}>
          <div className={styles.sectionTitleWithBadge}>
            <span>大模型 API 配置</span>
            {isLoading ? (
              <span className={styles.badgeLoading}>加载中…</span>
            ) : info?.has_key ? (
              <span className={styles.badgeOk}>已配置个人 Key</span>
            ) : (
              <span className={styles.badgeNone}>使用系统默认配置</span>
            )}
          </div>

          <div className={styles.presetRow}>
            <span className={styles.fieldLabel}>快速选择</span>
            <div className={styles.presetBtns}>
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  className={`${styles.presetBtn} ${provider === p.provider ? styles.presetActive : ''}`}
                  onClick={() => applyPreset(p.name)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.grid2}>
            <Input
              label="服务商"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="如 siliconflow / openai / deepseek"
            />
            <Input
              label="Base URL"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.example.com/v1"
            />
          </div>

          <div className={styles.grid2}>
            <Input
              label="模型名称"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="如 Qwen/Qwen2.5-7B-Instruct"
            />
            <Input
              label="视觉模型名称（用于图片识别，可选）"
              value={visionModel}
              onChange={(e) => setVisionModel(e.target.value)}
              placeholder="如 Qwen/Qwen2-VL-7B-Instruct"
            />
          </div>

          <Input
            label="API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={
              info?.has_key
                ? '已保存至当前账号，输入新值可覆盖'
                : 'sk-...'
            }
          />
          <p className={styles.apiKeyHint}>
            {info?.has_key
              ? `当前账号已保存 API Key，所有 AI 功能都会优先使用你的个人 Key。可直接输入新值覆盖，留空不会删除已保存的 Key。`
              : '未配置个人 Key 时，AI 功能会尝试使用系统默认 Key 或进入模拟模式。'}
          </p>

          <div className={styles.status}>
            {isLoading ? (
              <span className={styles.statusLoading}>加载中...</span>
            ) : info?.has_key ? (
              <span className={styles.statusOk}>已配置个人 API Key {info.updated_at ? `· 更新于 ${new Date(info.updated_at).toLocaleString()}` : ''}</span>
            ) : (
              <span className={styles.statusNone}>当前使用系统默认配置</span>
            )}
          </div>

          <div className={styles.actions}>
            <Button onClick={handleSaveLLM} loading={saving}>
              保存配置
            </Button>
            {info?.has_key && (
              <Button variant="danger" onClick={handleDeleteLLM} loading={deleting}>
                删除配置
              </Button>
            )}
          </div>

          <div className={styles.tips}>
            <strong>安全说明</strong>
            <ul>
              <li>你的 API Key 会在服务端加密存储，前端再次查看时不会显示明文。</li>
              <li>若未配置个人 Key，AI 功能将回退到系统默认 Key（如管理员已配置）或进入模拟模式。</li>
              <li>更换 Key 后立即生效，下一次 AI 评分 / 追问 / OCR 将使用新 Key。</li>
            </ul>
          </div>
        </div>
      </Card>

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
